/**
 * RegistrarAtividadeForm — form inline para registrar uma chamada/contato.
 *
 * Inspirado no cockpit (mockup): canal + assunto + descricao + resultado +
 * follow-up opcional. Submete via `onRegistrar(dados)` (AsyncButton no pai
 * trata toast). Cobre tambem o caso "Registrar chamada" (canal=TELEFONE).
 */
import React, { useState } from "react";
import { FiPlusCircle } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { CANAIS_ATIVIDADE } from "../../../services/crm";
import {
  Field,
  FormStack,
  InlineError,
  InlineHint,
  Input,
  Select,
  Textarea,
} from "../styles";

const RESULTADOS = [
  { value: "PENDENTE", label: "Pendente / em aberto" },
  { value: "SUCESSO", label: "Sucesso" },
  { value: "SEM_RESPOSTA", label: "Sem resposta" },
  { value: "REAGENDADO", label: "Reagendado" },
];

const VAZIO = {
  canal: "TELEFONE",
  assunto: "",
  descricao: "",
  resultado: "PENDENTE",
  proxima_acao: "",
  data_proxima_acao: "",
};

function RegistrarAtividadeForm({ onRegistrar, canalInicial }) {
  const [valores, setValores] = useState({
    ...VAZIO,
    canal: canalInicial || VAZIO.canal,
  });
  const [erro, setErro] = useState(null);

  const setCampo = (campo, valor) =>
    setValores((prev) => ({ ...prev, [campo]: valor }));

  const submeter = async () => {
    setErro(null);
    if (!valores.assunto.trim()) {
      setErro("Informe um assunto (ex.: 'Liguei para apresentar a proposta').");
      throw new Error("Assunto obrigatório.");
    }
    const dados = {
      canal: valores.canal,
      tipo: "FOLLOW_UP",
      assunto: valores.assunto.trim(),
      resultado: valores.resultado,
    };
    if (valores.descricao.trim()) dados.descricao = valores.descricao.trim();
    if (valores.proxima_acao.trim()) dados.proxima_acao = valores.proxima_acao.trim();
    if (valores.data_proxima_acao) dados.data_proxima_acao = valores.data_proxima_acao;
    await onRegistrar(dados);
    setValores({ ...VAZIO, canal: valores.canal });
  };

  return (
    <FormStack>
      <Field>
        Canal
        <Select
          value={valores.canal}
          onChange={(e) => setCampo("canal", e.target.value)}
        >
          {CANAIS_ATIVIDADE.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        Assunto *
        <Input
          type="text"
          value={valores.assunto}
          onChange={(e) => setCampo("assunto", e.target.value)}
          placeholder="Ex: Liguei para apresentar a proposta"
          maxLength={200}
        />
      </Field>

      <Field>
        Detalhes
        <Textarea
          value={valores.descricao}
          onChange={(e) => setCampo("descricao", e.target.value)}
          rows={2}
          placeholder="O que foi conversado, dores levantadas, próximos passos..."
        />
      </Field>

      <Field>
        Resultado
        <Select
          value={valores.resultado}
          onChange={(e) => setCampo("resultado", e.target.value)}
        >
          {RESULTADOS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        Próxima ação (follow-up)
        <Input
          type="text"
          value={valores.proxima_acao}
          onChange={(e) => setCampo("proxima_acao", e.target.value)}
          placeholder="Ex: Ligar na quinta para confirmar"
          maxLength={200}
        />
      </Field>

      <Field>
        Data do follow-up
        <Input
          type="date"
          value={valores.data_proxima_acao}
          onChange={(e) => setCampo("data_proxima_acao", e.target.value)}
        />
        <InlineHint>Opcional — alimenta o &quot;próximo follow-up&quot; do painel.</InlineHint>
      </Field>

      {erro && <InlineError>{erro}</InlineError>}

      <AsyncButton
        variant="secondary"
        onClick={submeter}
        successMessage="Atividade registrada."
        silentError
        style={{ alignSelf: "flex-start" }}
      >
        <FiPlusCircle size={14} aria-hidden="true" /> Registrar contato
      </AsyncButton>
    </FormStack>
  );
}

export default RegistrarAtividadeForm;
